import { useState } from 'react';
import { useT } from '@/i18n';
import { useAuthStore } from '@/state/authStore';
import { isSupabaseConfigured } from '@/lib/supabase';
import './AuthScreen.css';

type Mode = 'sign_in' | 'sign_up' | 'reset';

interface AuthScreenProps {
  /** Click handler for the "play without account" button. When set, a tertiary
   *  CTA shows below the auth form that lets the user jump straight into
   *  local-only play. Account conversion happens later via Settings. */
  onLocalPlay?: () => void;
}

export function AuthScreen({ onLocalPlay }: AuthScreenProps = {}) {
  const signInWithPassword = useAuthStore((s) => s.signInWithPassword);
  const signUpWithPassword = useAuthStore((s) => s.signUpWithPassword);
  const signInWithGoogle = useAuthStore((s) => s.signInWithGoogle);
  const resetPassword = useAuthStore((s) => s.resetPassword);
  const t = useT();

  const [mode, setMode] = useState<Mode>('sign_in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const configured = isSupabaseConfigured();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);

    if (mode === 'sign_in') {
      const result = await signInWithPassword(email, password);
      if (!result.ok) setError(result.error ?? 'Login fehlgeschlagen');
    } else if (mode === 'sign_up') {
      const result = await signUpWithPassword(email, password);
      if (!result.ok) setError(result.error ?? 'Registrierung fehlgeschlagen');
      else if (result.needsConfirmation) {
        setInfo('Bestätigungs-Mail gesendet. Bitte Postfach checken und Link klicken.');
      }
    } else if (mode === 'reset') {
      const result = await resetPassword(email);
      if (!result.ok) setError(result.error ?? 'Reset fehlgeschlagen');
      else setInfo('Reset-Link wurde gesendet, falls die E-Mail existiert.');
    }
    setBusy(false);
  };

  const handleGoogle = async () => {
    setBusy(true);
    setError(null);
    const result = await signInWithGoogle();
    if (!result.ok) setError(result.error ?? 'Google-Login fehlgeschlagen');
    setBusy(false);
  };

  return (
    <div className="auth-screen">
      <div className="auth-screen__bg" aria-hidden />
      <div className="auth-screen__content">
        <h1 className="auth-screen__title">{t.app.title}</h1>
        <p className="auth-screen__tagline">{t.app.tagline}</p>

        {!configured && (
          <div className="auth-screen__warning">
            ⚠ Supabase ist nicht konfiguriert. Lege <code>.env.local</code> mit
            <code> VITE_SUPABASE_URL</code> + <code>VITE_SUPABASE_ANON_KEY</code> an.
            <br />
            Siehe <code>supabase/README.md</code>.
          </div>
        )}

        <button
          type="button"
          className="auth-screen__google"
          onClick={handleGoogle}
          disabled={busy || !configured}
        >
          <span className="auth-screen__google-icon" aria-hidden>G</span>
          Mit Google fortfahren
        </button>

        <div className="auth-screen__divider">
          <span>oder</span>
        </div>

        <form className="auth-screen__form" onSubmit={submit}>
          <label className="auth-screen__label">
            <span>E-Mail</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              placeholder="runner@cyber.vault"
            />
          </label>

          {mode !== 'reset' && (
            <label className="auth-screen__label">
              <span>Passwort</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === 'sign_up' ? 'new-password' : 'current-password'}
                required
                minLength={6}
                placeholder="mindestens 6 Zeichen"
              />
            </label>
          )}

          {error && <div className="auth-screen__error">⛔ {error}</div>}
          {info && <div className="auth-screen__info">✓ {info}</div>}

          <button
            type="submit"
            className="btn btn--primary auth-screen__submit"
            disabled={busy || !configured}
          >
            {busy ? 'Lädt …' :
              mode === 'sign_in' ? 'Einloggen' :
              mode === 'sign_up' ? 'Account erstellen' :
              'Reset-Link senden'}
          </button>
        </form>

        <div className="auth-screen__links">
          {mode === 'sign_in' && (
            <>
              <button type="button" onClick={() => { setMode('sign_up'); setError(null); setInfo(null); }}>
                Account erstellen
              </button>
              <span>·</span>
              <button type="button" onClick={() => { setMode('reset'); setError(null); setInfo(null); }}>
                Passwort vergessen?
              </button>
            </>
          )}
          {mode === 'sign_up' && (
            <button type="button" onClick={() => { setMode('sign_in'); setError(null); setInfo(null); }}>
              Schon Account? Einloggen
            </button>
          )}
          {mode === 'reset' && (
            <button type="button" onClick={() => { setMode('sign_in'); setError(null); setInfo(null); }}>
              Zurück zum Login
            </button>
          )}
        </div>

        {onLocalPlay && (
          <>
            <div className="auth-screen__divider">
              <span>oder</span>
            </div>
            <button
              type="button"
              className="auth-screen__local"
              onClick={onLocalPlay}
              disabled={busy}
            >
              🎮 Ohne Konto spielen
              <span className="auth-screen__local-hint">
                Fortschritt nur auf diesem Gerät. Später jederzeit zu einem Konto umwandelbar.
              </span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
